import {useCallback, useEffect, useRef, useState} from 'react'

import {readSelectableApprovalModes, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {getApprovalModeConstraints} from '@github-ui/ahp-relay/relay-registry'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'

const APPROVAL_MODE_ERROR = 'Could not change tool permissions. Try again.'

export function useEstablishedApprovalModeMutation() {
  const session = useOptionalEstablishedAhpSession()
  const {passive, passiveKnown} = useOptionalEstablishedAhpSessionViewState()
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<string>()

  // Lets a settling request tell whether the session it was issued against is
  // still the mounted one, without reading a ref during render.
  const sessionRef = useRef(session)
  useEffect(() => {
    sessionRef.current = session
  }, [session])

  // Only the most recently started request may report a result. Without this a
  // slow first request can settle after a faster second one and overwrite the
  // newer outcome with a stale error, or clear pending while a change is still
  // in flight.
  const latestRequestRef = useRef(0)

  // Pending and error state describe one session's host, so carrying them across
  // a session swap would disable the picker, or show a failure, for a session
  // that never attempted the change. Adjusting during render rather than in an
  // effect avoids showing the stale state for a frame.
  const [observedSession, setObservedSession] = useState(session)
  if (observedSession !== session) {
    setObservedSession(session)
    if (isPending) setIsPending(false)
    if (error !== undefined) setError(undefined)
  }

  const onApprovalModeChange = useCallback(
    async (mode: SessionApprovalMode) => {
      if (!session || passive || !passiveKnown) return

      const requestId = ++latestRequestRef.current
      // A request only owns the result while it is both the newest request and
      // still aimed at the session that is mounted.
      const owns = () => latestRequestRef.current === requestId && sessionRef.current === session
      setIsPending(true)
      setError(undefined)
      try {
        const selectable = readSelectableApprovalModes(
          session.view.getSnapshot().sessionState,
          getApprovalModeConstraints(session.identity.taskId ?? session.identity.sessionId),
        )
        if (!selectable.selectable || !selectable.modes.includes(mode)) {
          throw new Error('The session does not allow this tool permission mode.')
        }
        await session.commands.setApprovalMode(mode)
        if (owns()) setIsPending(false)
      } catch (cause) {
        if (owns()) {
          setError(APPROVAL_MODE_ERROR)
          setIsPending(false)
        }
        throw cause
      }
    },
    [passive, passiveKnown, session],
  )

  return {
    error,
    isPending,
    onApprovalModeChange: session && passiveKnown && !passive ? onApprovalModeChange : undefined,
  }
}
